import { useState, type ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { SelectPopover } from '../../../components/SelectPopover';
import { TextInput } from '../../../components/TextInput';
import { fieldId, type KeyedOption } from './templateEditing';

interface FieldProps {
  label: string;
  /** API field path, e.g. `players[0].phone`. Drives the input id so errors can point at it. */
  path: string;
  error?: string;
  testId: string;
}

interface NumberFieldProps extends FieldProps {
  value: number | null;
  onChange: (value: number | null) => void;
  /** When true an empty box means `null`; otherwise an empty box keeps the last number. */
  nullable?: boolean;
  min?: number;
  max?: number;
  narrow?: boolean;
}

/**
 * A number box that holds its own text while the admin types.
 *
 * Offsets are often negative, and a fully controlled number input turns the leading "-" into 0
 * before the digit arrives. The draft string is kept locally and only a parseable value is sent up.
 */
export const NumberField = ({
  label,
  path,
  error,
  testId,
  value,
  onChange,
  nullable = false,
  min,
  max,
  narrow = true,
}: NumberFieldProps) => {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  const [committed, setCommitted] = useState(value);
  if (value !== committed) {
    // The value changed from outside (a reload or a row removal), so the draft follows it.
    setCommitted(value);
    setDraft(value === null ? '' : String(value));
  }

  const handleChange = (raw: string) => {
    setDraft(raw);
    const parsed = Number.parseInt(raw, 10);
    const next = Number.isNaN(parsed) ? (nullable && raw.trim() === '' ? null : undefined) : parsed;
    if (next === undefined) return;
    setCommitted(next);
    onChange(next);
  };

  return (
    <TextInput
      id={fieldId(path)}
      className={`sample-form__field${narrow ? ' sample-form__field--narrow' : ''}`}
      label={label}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      value={draft}
      error={error}
      data-testid={testId}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={() => setDraft(committed === null ? '' : String(committed))}
    />
  );
};

interface KeyedSelectProps extends FieldProps {
  options: KeyedOption[];
  value: string | null;
  onChange: (key: string) => void;
  placeholder: string;
  /** Offer a reset row back to the placeholder. Only for choices that may be left empty. */
  clearable?: boolean;
}

/** A labelled `SelectPopover` whose options are rows referenced by key rather than by label. */
export const KeyedSelect = ({
  label,
  path,
  error,
  testId,
  options,
  value,
  onChange,
  placeholder,
  clearable = false,
}: KeyedSelectProps) => {
  const id = fieldId(path);
  const errorId = `${id}-error`;
  const selected = options.find((option) => option.key === value);
  return (
    <div className="sample-form__field">
      <label className="sample-form__label" htmlFor={id}>
        {label}
      </label>
      <SelectPopover
        id={id}
        options={options.map((option) => option.label)}
        value={selected?.label ?? ''}
        onChange={(picked) => onChange(options.find((option) => option.label === picked)?.key ?? '')}
        placeholder={placeholder}
        describedById={error ? errorId : undefined}
        testId={testId}
        clearable={clearable}
        fullWidth
      />
      {error && (
        <span className="sample-form__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
};

interface TextAreaFieldProps extends FieldProps {
  value: string;
  onChange: (value: string) => void;
}

/** No textarea primitive exists yet, so this mirrors `TextInput`'s label and error wiring. */
export const TextAreaField = ({ label, path, error, testId, value, onChange }: TextAreaFieldProps) => {
  const id = fieldId(path);
  const errorId = `${id}-error`;
  return (
    <div className="sample-form__field sample-form__field--wide">
      <label className="sample-form__label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className="sample-form__textarea"
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && (
        <span className="sample-form__error" id={errorId}>
          {error}
        </span>
      )}
    </div>
  );
};

interface SampleFieldsetProps {
  legend: string;
  hint?: ReactNode;
  addLabel?: string;
  onAdd?: () => void;
  addTestId?: string;
  children: ReactNode;
}

/** One section of the template: a legend, optional hint, its rows, and an add button. */
export const SampleFieldset = ({ legend, hint, addLabel, onAdd, addTestId, children }: SampleFieldsetProps) => (
  <fieldset className="sample-form__group">
    <legend className="sample-form__legend">{legend}</legend>
    {hint}
    {children}
    {addLabel && onAdd && (
      <Button
        type="button"
        variant="secondary"
        className="sample-form__add"
        onClick={onAdd}
        data-testid={addTestId}
      >
        {addLabel}
      </Button>
    )}
  </fieldset>
);

interface SampleRowProps {
  /** Names the row for assistive tech, e.g. "Player 2". */
  label: string;
  removeLabel: string;
  onRemove: () => void;
  removeTestId: string;
  children: ReactNode;
}

/** A repeated row: its fields plus a remove button with a specific accessible name. */
export const SampleRow = ({ label, removeLabel, onRemove, removeTestId, children }: SampleRowProps) => (
  <div className="sample-form__row" role="group" aria-label={label}>
    <div className="sample-form__row-fields">{children}</div>
    <Button
      type="button"
      variant="secondary"
      className="sample-form__remove"
      aria-label={removeLabel}
      onClick={onRemove}
      data-testid={removeTestId}
    >
      Remove
    </Button>
  </div>
);
